import React, { Suspense } from 'react';
import Media from 'react-media';

import { HeaderTestIds } from '@trello/test-ids';
import classNames from 'classnames';

import { ScreenBreakpoints } from 'app/src/components/Responsive';
import SearchForm from 'app/gamma/src/components/search/form';
import printStyles from 'app/gamma/src/components/shared/print.less';

import { RouteNames } from 'app/common/router';
import BackToHomeLink from 'app/gamma/src/components/header/back-to-home-link';
import DesktopSettingsButton from 'app/gamma/src/components/header/desktop-settings-button';
import NotificationsMenuButton from 'app/gamma/src/components/header/notifications-menu-button';
import HeaderBoardMenuButton from './board-menu-button';
import ChannelSwitcherButton from './channel-switcher-button';
import { CreateMenuButton } from './create-menu-button';
import EnterpriseMenuButton from './enterprise-menu-button';
import HeaderLogo from './header-logo';
import { InfoMenuButton } from './info-menu-button';
import MemberMenuButton from './member-menu-button';
import { TacoAnnouncements } from 'app/src/components/TacoAnnouncements';

import { MemberModel } from 'app/gamma/src/types/models';

import styles from './header.less';
import { isDesktop } from '@trello/browser';
import { useLazyComponent } from '@trello/use-lazy-component';
import { useRouteContext } from 'app/src/useRouteContext';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import {
  useBoardsMenuVisibility,
  BoardsMenuVisibilityState,
} from 'app/src/components/BoardsMenuVisibility';

interface AuthenticatedHeaderProps {
  brandingColor: string | undefined;
  member: MemberModel | undefined;
}

export const AuthenticatedHeader: React.FunctionComponent<AuthenticatedHeaderProps> = ({
  member,
  brandingColor,
}) => {
  const { boardsMenuVisibility } = useBoardsMenuVisibility();
  const AtlassianAppSwitcherButton = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-app-switcher-button" */ 'app/gamma/src/components/header/atlassian-app-switcher-button'
      ),
    { namedImport: 'AtlassianAppSwitcherButton' },
  );

  const CrossFlowProvider = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "cross-flow-provider" */ 'app/src/components/CrossFlowProvider/CrossFlowProvider'
      ),
    { namedImport: 'CrossFlowProvider' },
  );

  const { routeName } = useRouteContext();

  return (
    // Desktop injects styles based on this `id`, but we'll update it
    // soon to use the new `data-desktop-id`. We'd rather use isDesktop
    // and isMac here to add another class, but we need to take the app's zoom
    // factor in to account which is only available from within the
    // desktop context.
    <div
      className={classNames(styles.header, printStyles.noPrint)}
      id="header"
      data-test-id={HeaderTestIds.Container}
      data-desktop-id="header-inner"
      style={{ backgroundColor: brandingColor }}
    >
      <div className={styles.leftSection}>
        <Suspense fallback={null}>
          <ChunkLoadErrorBoundary fallback={null}>
            <CrossFlowProvider>
              <AtlassianAppSwitcherButton member={member} />
            </CrossFlowProvider>
          </ChunkLoadErrorBoundary>
        </Suspense>
        <BackToHomeLink />
        {boardsMenuVisibility !== BoardsMenuVisibilityState.PINNED && (
          <HeaderBoardMenuButton />
        )}
        {routeName !== RouteNames.SEARCH && <SearchForm />}
        <TacoAnnouncements />
      </div>
      <HeaderLogo />
      <div className={styles.rightSection}>
        <CreateMenuButton />
        {!isDesktop() && <InfoMenuButton />}
        <NotificationsMenuButton />
        <EnterpriseMenuButton />
        <Media query={ScreenBreakpoints.MediumMin}>
          {(matches: boolean) =>
            matches && (
              <div className={styles.flexContainer}>
                <ChannelSwitcherButton />
              </div>
            )
          }
        </Media>
        <MemberMenuButton />
        {isDesktop() && <DesktopSettingsButton />}
      </div>
    </div>
  );
};
